import { useEffect } from "react";

import type {
  AgenticLogicActiveRepairRun,
  AgenticLogicPublishGateKey,
  AgenticLogicRulePublishJobDto,
  AgenticLogicRuleSettings,
} from "@intelligems/ig-types";
import type { AgenticLogicDataSurface } from "@intelligems/ig-utils";
import { useQueryClient } from "@tanstack/react-query";
import { useImmer } from "use-immer";

import { useAdoptAgenticLogicRuleGeneratedTests } from "@src/api/agentic-logic/adopt-agentic-logic-generated-tests";
import { useAgenticLogicPublishProgress } from "@src/api/agentic-logic/agentic-logic-publish-progress";
import { useAgenticLogicRuleGeneratedTests } from "@src/api/agentic-logic/get-agentic-logic-generated-tests";
import { useAgenticLogicRuleDraft } from "@src/api/agentic-logic/get-agentic-logic-rule-draft";
import { useAgenticLogicRule } from "@src/api/agentic-logic/get-agentic-logic-rule";
import { useAgenticLogicRuleVersions } from "@src/api/agentic-logic/get-agentic-logic-rule-versions";
import { useDeleteAgenticLogicRuleVersion } from "@src/api/agentic-logic/delete-agentic-logic-rule-version";
import { useImportAgenticLogicRuleVersionDraft } from "@src/api/agentic-logic/import-agentic-logic-rule-version-draft";
import {
  useCancelAgenticLogicRuleTestRun,
  usePublishAgenticLogicRule,
  useRunAgenticLogicRuleGates,
} from "@src/api/agentic-logic/publish-agentic-logic-rule";
import { useRollbackAgenticLogicRule } from "@src/api/agentic-logic/rollback-agentic-logic-rule";
import { useSaveAgenticLogicRuleDraft } from "@src/api/agentic-logic/save-agentic-logic-rule-draft";
import { useCreateOrgSecret } from "@src/api/org-secrets/create-org-secret";
import { useDeleteOrgSecret } from "@src/api/org-secrets/delete-org-secret";
import { useOrgSecrets } from "@src/api/org-secrets/list-org-secrets";
import { agenticLogicKeys } from "@src/api/queryKeys";
import type {
  WorkspaceApiPort,
  WorkspaceMutationCallbacks,
  WorkspaceRunInput,
  WorkspaceSaveDraftInput,
} from "@src/features/agenticCodegen/workspace/apiPort";
import type {
  WorkspaceDependency,
  WorkspaceVersion,
} from "@src/features/agenticCodegen/workspace/types";

import { AGENTIC_LOGIC_ENTRY_FILENAME } from "./agenticLogicMonacoTypes";
import { toWorkspaceVersion } from "./ruleVersions";

/**
 * The draft body's `source`, or `null` when the entry file is absent.
 *
 * Agentic Logic is single-file: the backend stores `source` as the entry file's
 * contents verbatim. An entry present but EMPTY is a legitimate save — the
 * author cleared the editor. An entry that is ABSENT is a shell/port contract
 * break, and coercing it to `""` would PUT an empty source over the merchant's
 * saved rule: the mutation succeeds, the query cache reseeds from the response,
 * and the code is gone with nothing reported anywhere. Hence `null`, so the
 * caller fails the save instead of destroying it.
 *
 * No path reaches `null` today — the decoder always emits the entry key, the
 * no-draft path seeds it, and delete/rename refuse it — and the shell's only
 * `saveDraft` call site passes no `onError`, so the refusal is currently a
 * silent no-op rather than a reported failure. That is the right trade against
 * a silent overwrite, but if a real caller ever trips it, thread an `onError`
 * through so the author is told the save did not happen.
 */
export function agenticDraftSource(
  files: Record<string, string>
): string | null {
  return files[AGENTIC_LOGIC_ENTRY_FILENAME] ?? null;
}

/**
 * Agentic-Logic-only reads the shared view model has no notion of. Returned
 * alongside the port, mirroring `useShippingApiPort`, so the whole binding runs
 * one rule query rather than two observers on the same key.
 */
export interface AgenticWorkspaceExtras {
  publishJob: AgenticLogicRulePublishJobDto | undefined;
  publishOutcomePending: boolean;
  /** Plain-language rule summary shown and edited beside the workspace. */
  ruleDescription: string | null;
  /**
   * The rule's recommendation settings, editable in the page header. Null only
   * while the rule query is in flight — every rule has an answer to all four.
   */
  ruleSettings: AgenticLogicRuleSettings | null;
  /**
   * What the acting user may author against the org-data surface — what types
   * `sdk.getDataset()` and `sdk.getSource()` for this author and org.
   */
  dataSurface: AgenticLogicDataSurface;
  /**
   * The rule's repair run (active or most recently finished), and whether
   * the loop is still working. Drives the "Atlas is fixing this" strip
   * state, the canvas pending signal, and the give-up panel.
   */
  repair: {
    run: AgenticLogicActiveRepairRun | null;
    isActive: boolean;
    /**
     * `true` until the version history has resolved. A stopped run is judged
     * against the live version's publish time, and that judgement cannot be
     * made before the versions arrive — the rule detail lands first, and
     * deciding on it alone flashed the give-up panel on every open.
     */
    isVersionHistoryLoading: boolean;
  };
}

/**
 * Builds the Agentic Logic binding of `WorkspaceApiPort`.
 *
 * It supplies `gateRun` and none of the other optional capability groups: there
 * is no AI test generation, no import-as-draft, and no unpublish among the
 * deployed routes. Each absence degrades exactly one affordance in the shell
 * rather than half-breaking a shared path.
 */
export const useAgenticApiPort = (
  ruleId: string | undefined,
  dataSurface: AgenticLogicDataSurface
): {
  port: WorkspaceApiPort<AgenticLogicPublishGateKey>;
  extras: AgenticWorkspaceExtras;
} => {
  const ruleQuery = useAgenticLogicRule(ruleId);
  const draftQuery = useAgenticLogicRuleDraft(ruleId);
  const generatedTestsQuery = useAgenticLogicRuleGeneratedTests(ruleId);
  const versionsQuery = useAgenticLogicRuleVersions(ruleId);
  const orgSecretsQuery = useOrgSecrets();
  const activeRepairRun = ruleQuery.data?.activeRepairRun ?? null;
  // Every rule state, deliberately — a rule's checks are durable evidence, not
  // draft-only chrome. The hook ranks session job → live repair job → durable
  // job, so a draft mid-publish is unaffected, while an active or paused rule
  // falls through to the run that produced its current state.
  const progress = useAgenticLogicPublishProgress(
    ruleId,
    ruleQuery.data?.latestPublishJob ?? null,
    activeRepairRun
  );

  const saveDraftMutation = useSaveAgenticLogicRuleDraft();
  const publishMutation = usePublishAgenticLogicRule();
  const runGatesMutation = useRunAgenticLogicRuleGates();
  const cancelTestRunMutation = useCancelAgenticLogicRuleTestRun();
  const rollbackMutation = useRollbackAgenticLogicRule();
  const deleteVersionMutation = useDeleteAgenticLogicRuleVersion();
  const importVersionMutation = useImportAgenticLogicRuleVersionDraft();
  const adoptTestsMutation = useAdoptAgenticLogicRuleGeneratedTests();
  const createSecretMutation = useCreateOrgSecret();
  const deleteSecretMutation = useDeleteOrgSecret();

  const queryClient = useQueryClient();

  const rule = ruleQuery.data?.rule ?? null;
  const draft = draftQuery.data?.draft ?? null;

  // Declared dependencies are edited locally and persisted with the draft. The
  // shell keeps `@tanstack/react-form` out of itself, so the port owns this
  // state; CSP holds the equivalent in its controls form.
  const [dependencies, setDependencies] = useImmer<WorkspaceDependency[]>([]);
  const [isDependenciesDirty, setIsDependenciesDirty] = useImmer(false);

  // Re-seed only when a save actually moves `lastUpdateTs`, never on background
  // refetches — which would otherwise discard unsaved dependency edits.
  useEffect(() => {
    setDependencies(() => draft?.dependencies ?? []);
    setIsDependenciesDirty(() => false);
  }, [draft?.lastUpdateTs, setDependencies, setIsDependenciesDirty]);

  const versions: WorkspaceVersion[] = (versionsQuery.data?.versions ?? []).map(
    toWorkspaceVersion
  );
  const activeVersion =
    versions.find((version) => version.status === "active") ?? null;

  // No Agentic Logic response carries `availableSecretNames`, so the org's
  // secret list is the source for both the reference list and the manager.
  const secrets = orgSecretsQuery.data?.secrets ?? [];

  const port: WorkspaceApiPort<AgenticLogicPublishGateKey> = {
    entityRef: ruleId,
    entity: {
      name: rule?.name ?? "",
      statusLabel: rule?.state ?? null,
      // A rule is writable for its whole life: retiring one deletes it rather
      // than freezing it, so there is no state that reads back read-only.
      isReadOnly: false,
    },

    isLoading: ruleQuery.isLoading || draftQuery.isLoading,
    draft: draft
      ? {
          lastUpdateTs: draft.lastUpdateTs,
          updatedByUserId: draft.updatedByUserId,
          updatedByUser: draft.updatedByUser,
          origin: draft.origin,
        }
      : null,
    // Inline, unlike CSP's presigned URL: there is no companion fetch, so the
    // source is never separately "loading" once the draft has resolved.
    source: {
      data: draft?.source ?? undefined,
      isLoading: draftQuery.isLoading,
    },
    // Inline for the same reason as `source`: the draft DTO carries the suite,
    // so there is no separate artifact to fetch. `undefined` until the draft
    // resolves, then `null` when the rule genuinely has no suite saved.
    userTests: {
      data: draft ? (draft.userTestSuite ?? null) : undefined,
      isLoading: draftQuery.isLoading,
    },
    // Inline for the same reason, and now the SOURCE OF TRUTH for the suite:
    // the publish pipeline writes each resolved suite back onto the draft, so
    // what an author reloads is what the next run will reuse.
    generatedTests: {
      data: draft ? (draft.generatedTestSuite ?? null) : undefined,
      isLoading: draftQuery.isLoading,
    },
    versions,
    activeVersion,
    availableSecretNames: secrets.map((secret) => secret.name),
    secrets,

    controls: {
      dependencies,
      isDirty: isDependenciesDirty,
      addDependency: (dependency) => {
        setDependencies((current) => {
          current.push(dependency);
        });
        setIsDependenciesDirty(() => true);
      },
      removeDependency: (name) => {
        setDependencies((current) =>
          current.filter((dependency) => dependency.name !== name)
        );
        setIsDependenciesDirty(() => true);
      },
      updateDependencyVersion: (name, version) => {
        setDependencies((current) => {
          const match = current.find((dependency) => dependency.name === name);
          if (match) {
            match.version = version;
          }
        });
        setIsDependenciesDirty(() => true);
      },
    },

    publishJob: {
      trackedJobId: progress.trackedJobId,
      job: progress.job,
      // The poll/websocket seam: there is no agentic websocket topic, so a
      // frame exists exactly when a poll has landed.
      hasProgressFrame: progress.hasProgressFrame,
    },
    trackJob: (jobId) => {
      if (jobId === null) {
        progress.clearTrackedJob();
      } else {
        progress.trackJob(jobId);
      }
    },

    saveDraft: (
      input: WorkspaceSaveDraftInput,
      callbacks: WorkspaceMutationCallbacks
    ) => {
      if (!ruleId) {
        return;
      }
      const source = agenticDraftSource(input.files);
      if (source === null) {
        callbacks.onError?.();
        return;
      }
      saveDraftMutation.mutate(
        {
          pathParams: { ruleId },
          queryParams: undefined,
          body: {
            source,
            userTestSuite: input.userTestSuite,
            // Omitted when untouched, exactly like validationScenarios below:
            // sending it would overwrite the pipeline's own write-back with a
            // stale editor buffer, and an explicit suite is stored as
            // hand-authored (the next run proves it rather than trusting it).
            ...(input.generatedTestSuite === null
              ? {}
              : { generatedTestSuite: input.generatedTestSuite }),
            // Always empty, never `input.dependencies`: Agentic Logic admits
            // no npm package and the save schema refuses a non-empty list, so
            // a legacy draft's stored list is cleared by the next save rather
            // than round-tripped into a request the API would reject.
            dependencies: [],
            // Omitted when the source was untouched this session so the stored
            // provenance survives dependency-only saves.
            ...(input.origin === null ? {} : { origin: input.origin }),
            // validationScenarios is deliberately OMITTED, not sent empty.
            // Omitting carries the draft's existing scenarios forward; sending
            // `[]` would discard them, so every code save from this editor
            // would silently drop what an author chose to test against.
          },
        },
        {
          onSuccess: () => {
            setIsDependenciesDirty(() => false);
            callbacks.onSuccess?.();
          },
          onError: () => callbacks.onError?.(),
        }
      );
    },
    isSavingDraft: saveDraftMutation.isPending,

    publish: (
      input: WorkspaceRunInput,
      callbacks: WorkspaceMutationCallbacks<{ publishJobId: string }>
    ) => {
      if (!ruleId) {
        return;
      }
      publishMutation.mutate(
        {
          pathParams: { ruleId },
          queryParams: undefined,
          body: {
            ...(input.notes?.trim() ? { notes: input.notes.trim() } : {}),
            ...(input.title?.trim() ? { title: input.title.trim() } : {}),
          },
        },
        { onSuccess: (data) => callbacks.onSuccess?.(data) }
      );
    },
    isPublishing: publishMutation.isPending,

    rollbackToVersion: (version, callbacks) => {
      if (!ruleId) {
        return;
      }
      rollbackMutation.mutate(
        {
          // The version travels in the BODY here, not the path.
          pathParams: { ruleId },
          queryParams: undefined,
          body: { version },
        },
        { onSuccess: () => callbacks.onSuccess?.() }
      );
    },
    isRollingBack: rollbackMutation.isPending,

    deleteVersion: (version) => {
      if (!ruleId) {
        return;
      }
      deleteVersionMutation.mutate({
        pathParams: { ruleId, version: String(version) },
        queryParams: undefined,
        body: {},
      });
    },
    isDeletingVersion: deleteVersionMutation.isPending,

    createSecret: (name, value) => {
      createSecretMutation.mutate({
        pathParams: undefined,
        queryParams: undefined,
        body: { name, value },
      });
    },
    deleteSecret: (id) => {
      deleteSecretMutation.mutate({
        pathParams: { id },
        queryParams: undefined,
        body: {},
      });
    },
    isMutatingSecret:
      createSecretMutation.isPending || deleteSecretMutation.isPending,

    onPublishSucceeded: () => {
      if (!ruleId) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rule(ruleId),
      });
      void queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.versions(ruleId),
      });
    },

    suiteInventory: {
      generated: {
        data: generatedTestsQuery.data,
        isLoading: generatedTestsQuery.isLoading,
        isError: generatedTestsQuery.isError,
      },
      adoptTests: (testIds, callbacks) => {
        if (!ruleId) {
          return;
        }
        adoptTestsMutation.mutate(
          {
            pathParams: { ruleId },
            queryParams: undefined,
            // Compare-and-set against the draft this inventory was loaded
            // from; a draft written since is refused as `stale_draft`.
            body: { testIds, expectedLastUpdateTs: draft?.lastUpdateTs ?? 0 },
          },
          {
            // The mutation hook has already seeded the draft and inventory
            // caches from this response.
            onSuccess: (data) =>
              callbacks.onSuccess?.({
                userTestSuite: data.draft.userTestSuite ?? "",
                userTests: data.userTests,
                adoptedTestIds: data.adoptedTestIds,
              }),
            onError: () => callbacks.onError?.(),
          }
        );
      },
    },

    // --- optional capability groups ------------------------------------------
    gateRun: {
      // The dry-run body carries no suites: unlike the shipping provider, a rule
      // persists both its AI and user suites on the draft, and the shell only
      // enables this action while the draft is clean — so the server already has
      // exactly what the editor is showing. `input` is therefore unused here.
      runGates: (_input, callbacks) => {
        if (!ruleId) {
          return;
        }
        // One call. There is no recording step to sequence in front of it any
        // more: the gates read the org's live data, and the server supplies a
        // default scenario when the draft authored none.
        runGatesMutation.mutate(
          { pathParams: { ruleId }, queryParams: undefined, body: {} },
          {
            onSuccess: (data) => callbacks.onSuccess?.(data),
            onError: () => callbacks.onError?.(),
          }
        );
      },
      isRunningGates: runGatesMutation.isPending,
    },

    testRunCancellation: {
      cancelRun: (jobId, callbacks) => {
        if (!ruleId) {
          return;
        }
        cancelTestRunMutation.mutate(
          { ruleId, jobId },
          {
            onSuccess: () => callbacks.onSuccess?.(),
            onError: () => callbacks.onError?.(),
          }
        );
      },
      isCancelling: cancelTestRunMutation.isPending,
    },

    versionImport: {
      importVersion: (version, fetchArtifacts, onImported) => {
        if (!ruleId) {
          return;
        }
        importVersionMutation.mutate(
          {
            pathParams: { ruleId, version: String(version) },
            queryParams: undefined,
            body: {},
          },
          {
            onSuccess: async (data) => {
              const importedDraft = data.draft;
              const imported = {
                source: importedDraft.source,
                userTests: importedDraft.userTestSuite,
                lastUpdateTs: importedDraft.lastUpdateTs,
                dependencies: importedDraft.dependencies,
              };
              // Inline, so this resolves without a network round trip — see
              // WorkspaceImportedDraft. It still runs through the shell's
              // fetcher so both domains keep one ordering: artifacts resolved,
              // cache seeded, then local editor state reseeded.
              const artifacts = await fetchArtifacts(imported);

              // Seeded rather than invalidated: the response IS the draft, and
              // the editor reseeds from this cache entry the moment
              // `filesOverride` clears. A refetch here would race that reseed.
              //
              // Seeding also moves `lastUpdateTs`, which is what the
              // dependency re-seed effect above keys on — so the declared
              // dependencies follow the import without a second write here.
              queryClient.setQueryData(agenticLogicKeys.draft(ruleId), {
                draft: importedDraft,
              });

              // A SHALLOW COPY, not `imported` itself. The two callbacks are
              // handed equal but DISTINCT top-level objects, so neither can
              // observe the other's mutations and neither can identity-compare
              // its way to the other's. Nested values stay shared by reference,
              // exactly as two separate literals over the same draft would be.
              onImported({ ...imported }, artifacts);
            },
          }
        );
      },
      isImportingVersion: importVersionMutation.isPending,
    },

    // No aiTests or unpublish — see the doc comment.
  };

  const extras: AgenticWorkspaceExtras = {
    publishJob: progress.job,
    // The job poll can finish before rule detail observes the repair handoff.
    publishOutcomePending:
      !ruleQuery.isError &&
      progress.job?.status === "failed" &&
      (ruleQuery.data?.latestPublishJob?.id !== progress.job.id ||
        ruleQuery.data.latestPublishJob.status !== "failed") &&
      activeRepairRun?.currentPublishJobId !== progress.job.id,
    ruleDescription: rule?.description ?? null,
    ruleSettings: rule?.settings ?? null,
    dataSurface,
    repair: {
      run: activeRepairRun,
      isActive:
        activeRepairRun !== null && activeRepairRun.finishedAtTs === null,
      isVersionHistoryLoading: versionsQuery.isLoading,
    },
  };

  return { port, extras };
};
